import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { TextInput } from '../../components/ui/form/TextInput';
import { useDocumentTitle } from '../../lib/title';
import { usePlayer } from '../../stores/player';
import { Artwork } from '../dashboard/Artwork';
import { duration } from '../rooms/api';
import { AddToPlaylist } from './AddToPlaylist';
import { usePlaylist } from './usePlaylist';

/**
 * A playlist, on a page of its own.
 *
 * ## Why it is not the album page
 *
 * In the database they are the same row - a `collection` with a `type` - and
 * that is right: they hold ordered tracks, they have a title and a visibility,
 * and one set of endpoints serves both. It does not follow that they are the
 * same *page*.
 *
 * An album is a release. It has a cover somebody commissioned, an artist whose
 * name belongs at the top, and a running order that is part of the work. A
 * playlist is a list somebody made, usually from other people's records, and
 * the interesting thing about it is what is in it and what order they put it
 * in. Rendering the second as the first gave playlists a big empty artwork slot
 * and an "artist" heading for a list of eleven different artists.
 *
 * ## The editing lives in `usePlaylist`
 *
 * Shared with the panel in the library dock, so reordering here and reordering
 * there cannot come to mean different things.
 */
export function PlaylistPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const playlistId = Number(id);

  const { data, isPending, isError, tracks, mine, reorder, drop, rename, destroy } =
    usePlaylist(playlistId);

  useDocumentTitle(data?.title ?? null);

  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');

  if (isPending) {
    return <LoadingPanel label="Opening" className="mt-16" />;
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[900px] px-6 py-16 text-soft">
        That playlist is not here. It may have been deleted, or it may be private.
      </div>
    );
  }

  const first = tracks[0];
  const isCurrentList = first !== undefined && current?.id === first.id;

  /* Moving a row is a swap with its neighbour, and the whole order is sent. */
  const move = (index: number, by: -1 | 1) => {
    const next = [...tracks];
    const target = index + by;

    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target]!, next[index]!];
    reorder.mutate(next.map((track) => track.id));
  };

  return (
    <div className="mx-auto flex max-w-[900px] flex-col gap-6 px-6 py-8" data-playlist-page>
      <header className="flex flex-col gap-3">
        <span className="meta-caps text-faint">Playlist</span>

        {renaming ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();

              if (draft.trim()) {
                rename.mutate(draft.trim(), { onSuccess: () => setRenaming(false) });
              }
            }}
          >
            <TextInput
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              aria-label="Playlist name"
              autoFocus
              data-playlist-rename-input
              className="max-w-sm"
            />
            <Button type="submit" loading={rename.isPending}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <h1 className="text-3xl font-bold tracking-tight text-ink" data-playlist-title>
            {data.title}
          </h1>
        )}

        <p className="font-mono text-[11px] text-faint">
          {data.owner?.display_name ?? data.owner?.username ?? 'somebody'} ·{' '}
          {tracks.length === 1 ? '1 track' : `${tracks.length} tracks`}
          {data.visibility !== 'public' && ` · ${data.visibility}`}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={tracks.length === 0}
            data-playlist-play
            onClick={() =>
              isCurrentList && playing ? toggle() : void play(first!, tracks, 'collection')
            }
          >
            {isCurrentList && playing ? 'Pause' : 'Play'}
          </Button>

          {mine && !renaming && (
            <Button
              variant="ghost"
              data-playlist-rename
              onClick={() => {
                setDraft(data.title);
                setRenaming(true);
              }}
            >
              Rename
            </Button>
          )}

          {mine && (
            <Button
              variant="ghost"
              data-playlist-delete
              loading={destroy.isPending}
              onClick={() => destroy.mutate(undefined, { onSuccess: () => navigate('/') })}
            >
              Delete
            </Button>
          )}
        </div>
      </header>

      {tracks.length === 0 ? (
        <Card className="p-6 text-sm text-soft">
          Nothing in here yet. The plus beside any track adds it — on a profile, a
          chart, a room's history, or the player bar.
        </Card>
      ) : (
        <ul className="flex flex-col gap-1" data-playlist-tracks>
          {tracks.map((track, index) => {
            const isCurrent = current?.id === track.id;

            return (
              <li
                key={track.id}
                data-playlist-track={track.id}
                className="group flex items-center gap-3 rounded-[var(--radius-control)] p-2 transition-colors hover:bg-raised"
              >
                <span className="w-5 shrink-0 text-right font-mono text-[11px] text-faint">
                  {index + 1}
                </span>

                <button
                  type="button"
                  aria-label={`Play ${track.title}`}
                  onClick={() =>
                    isCurrent ? toggle() : void play(track, tracks.slice(index), 'collection')
                  }
                  className="relative h-10 w-10 shrink-0 overflow-hidden rounded-[var(--radius-chip)]"
                >
                  <Artwork track={track} className="h-full w-full" />
                  <span
                    aria-hidden
                    className={`absolute inset-0 flex items-center justify-center bg-veil transition-opacity ${
                      isCurrent && playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                  >
                    <svg viewBox="0 0 20 20" className="h-4 w-4 text-white" fill="currentColor" aria-hidden>
                      <path d="M6 4.5v11l10-5.5z" />
                    </svg>
                  </span>
                </button>

                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${isCurrent ? 'text-accent' : 'text-ink'}`}>
                    {track.title}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-faint">
                    {track.owner?.display_name ?? track.owner?.username ?? ''}
                  </span>
                </span>

                <span className="shrink-0 font-mono text-[11px] text-faint">
                  {duration(track.duration_ms)}
                </span>

                {/*
                  Not only for the owner. Somebody reading a playlist that is
                  not theirs has no other way to take one track out of it, and
                  that is most of what a shared playlist is for.
                */}
                <AddToPlaylist trackId={track.id} title={track.title} size="sm" />

                {/*
                  Arrows rather than drag, for the reason the dock's editor gives:
                  a drag target this size is a coin toss on a trackpad, and it is
                  unusable with a keyboard.
                */}
                {mine && (
                  <span className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <button
                      type="button"
                      aria-label={`Move ${track.title} up`}
                      disabled={index === 0 || reorder.isPending}
                      onClick={() => move(index, -1)}
                      className="px-1 text-xs text-soft hover:text-ink disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${track.title} down`}
                      disabled={index === tracks.length - 1 || reorder.isPending}
                      onClick={() => move(index, 1)}
                      className="px-1 text-xs text-soft hover:text-ink disabled:opacity-30"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${track.title}`}
                      data-playlist-remove={track.id}
                      onClick={() => drop.mutate(track.id)}
                      className="px-1 text-xs text-soft hover:text-hot-text"
                    >
                      ×
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
